import {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useContext,
  useCallback,
  useRef,
} from 'react';
import { useAppDispatch } from '../redux/hooks';
import { useNavigate } from 'react-router-dom';
import { setLoginState, setLogoutState } from '../redux/slices/authentication.slice';
import { AuthService, AuthUser } from '@src/services/auth.service';
import api from '@src/utils/api';
import {
  beginSessionActivity,
  endSessionActivity,
  INACTIVITY_LIMIT_MS,
  LogoutReason,
  readSessionActivity,
  sessionIsCurrent,
  watchSessionActivity,
} from './sessionActivity';
import SessionWarning from './SessionWarning';

interface AuthContextType {
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  loading: boolean;
  logout: () => Promise<void>;
  logoutReason: LogoutReason | null;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [logoutReason, setLogoutReason] = useState<LogoutReason | null>(null);
  const [remaining, setRemaining] = useState(300);
  const generation = useRef(0);
  const mounted = useRef(false);
  const logoutRequest = useRef<Promise<void> | null>(null);
  const activity = useRef<ReturnType<typeof watchSessionActivity> | null>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const revokeCookies = useCallback(() => {
    if (!logoutRequest.current) {
      const request = AuthService.logout().then(() => undefined);
      logoutRequest.current = request;
      void request
        .finally(() => {
          if (logoutRequest.current === request) logoutRequest.current = null;
        })
        .catch(() => undefined);
    }
    return logoutRequest.current;
  }, []);

  const clearAuth = useCallback(
    (reason: LogoutReason) => {
      generation.current++;
      activity.current?.stop();
      setUser(null);
      setLoading(false);
      setRemaining(300);
      setLogoutReason(reason);
      dispatch(setLogoutState());
      navigateRef.current('/login', { replace: true });
    },
    [dispatch],
  );

  const logout = useCallback(async () => {
    try {
      endSessionActivity('logout');
    } finally {
      clearAuth('logout');
      await revokeCookies();
    }
  }, [clearAuth, revokeCookies]);

  const handleSessionLogout = useCallback(
    (reason: LogoutReason, notifyServer: boolean) => {
      clearAuth(reason);
      if (notifyServer) void revokeCookies().catch(() => undefined);
    },
    [clearAuth, revokeCookies],
  );

  const rejectExpiredSession = useCallback(() => {
    const stored = readSessionActivity();
    const expired = Boolean(
      stored && !stored.logoutReason && Date.now() - stored.lastActivity >= INACTIVITY_LIMIT_MS,
    );
    if (expired) endSessionActivity('inactivity');
    handleSessionLogout(stored?.logoutReason ?? (expired ? 'inactivity' : 'logout'), expired);
  }, [handleSessionLogout]);

  useEffect(() => {
    let cancelled = false;
    const version = generation.current;
    const fetchUser = async () => {
      try {
        const stored = readSessionActivity();
        if (stored?.logoutReason) {
          setLogoutReason(stored.logoutReason);
          dispatch(setLogoutState());
          return;
        }
        if (stored && Date.now() - stored.lastActivity >= INACTIVITY_LIMIT_MS) {
          endSessionActivity('inactivity');
          handleSessionLogout('inactivity', true);
          return;
        }
        const { user: currentUser } = await AuthService.getMe();
        if (cancelled || version !== generation.current) return;
        if (currentUser) {
          // A page reload adopts the shared deadline; it never counts as activity.
          if (!readSessionActivity()) {
            if (stored) {
              handleSessionLogout('logout', false);
              return;
            }
            beginSessionActivity(currentUser.id);
          }
          if (!sessionIsCurrent(currentUser.id)) {
            rejectExpiredSession();
            return;
          }
          setUser(currentUser);
          dispatch(setLoginState({ user: currentUser }));
        } else {
          dispatch(setLogoutState());
        }
      } catch {
        if (!cancelled && version === generation.current) {
          setUser(null);
          dispatch(setLogoutState());
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void fetchUser();
    return () => {
      cancelled = true;
    };
  }, [dispatch, handleSessionLogout, rejectExpiredSession]);

  const userId = user?.id;
  const updateRemaining = useCallback((seconds: number) => {
    setRemaining(seconds <= 60 ? seconds : 300);
  }, []);
  useEffect(() => {
    if (userId === undefined) return;
    const watcher = watchSessionActivity(userId, updateRemaining, handleSessionLogout);
    activity.current = watcher;
    return () => {
      watcher.stop();
      activity.current = null;
    };
  }, [userId, updateRemaining, handleSessionLogout]);

  const login = async (email: string, password: string) => {
    // A delayed logout response must not clear the cookies of a new login.
    await logoutRequest.current?.catch(() => undefined);
    if (!mounted.current) return;
    const version = ++generation.current;
    const data = await AuthService.login(email, password);
    if (!mounted.current || version !== generation.current) return;
    try {
      beginSessionActivity(data.user.id);
    } catch (error) {
      clearAuth('logout');
      await revokeCookies().catch(() => undefined);
      throw error;
    }
    setLogoutReason(null);
    setRemaining(300);
    setUser(data.user);
    dispatch(setLoginState({ user: data.user }));
    navigate('/');
  };

  const refreshUser = async () => {
    const version = generation.current;
    try {
      const { data } = await api.get<{ user: AuthUser | null }>('/jwt/auth/me');
      const currentUser = data.user;
      if (!mounted.current || version !== generation.current) return;
      if (currentUser && sessionIsCurrent(currentUser.id)) {
        setUser(currentUser);
        dispatch(setLoginState({ user: currentUser }));
      } else {
        rejectExpiredSession();
      }
    } catch {
      if (mounted.current && version === generation.current) clearAuth('logout');
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, refreshUser, loading, logoutReason }}>
      {children}
      <SessionWarning
        open={Boolean(user) && remaining <= 60}
        remaining={remaining}
        onContinue={() => activity.current?.continueSession()}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
